import { supabase } from '../lib/supabase';
import type { Deliverable, DeliverableWithInfluencer, DeliverableStatus } from '../types/multiInfluencer';
import { log } from '../lib/logger';

export const deliverableService = {
  /**
   * Get all deliverables for a campaign
   */
  async getByCampaign(campaignId: string): Promise<DeliverableWithInfluencer[]> {
    const { data, error } = await supabase
      .from('deliverables')
      .select(`
        *,
        campaign_influencers!left(influencer_name)
      `)
      .eq('campaign_id', campaignId)
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) throw error;

    return (data || []).map((d: any) => ({
      ...d,
      influencer_name: d.campaign_influencers?.influencer_name || null,
      days_until_due: d.due_date ? this.calculateDaysUntilDue(d.due_date) : null,
    }));
  },

  /**
   * Get deliverables for a specific campaign influencer
   */
  async getByInfluencer(campaignInfluencerId: string): Promise<Deliverable[]> {
    const { data, error } = await supabase
      .from('deliverables')
      .select('*')
      .eq('campaign_influencer_id', campaignInfluencerId)
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get unassigned deliverables for a campaign (no campaign_influencer_id)
   */
  async getUnassignedByCampaign(campaignId: string): Promise<Deliverable[]> {
    const { data, error } = await supabase
      .from('deliverables')
      .select('*')
      .eq('campaign_id', campaignId)
      .is('campaign_influencer_id', null)
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new deliverable
   */
  async create(data: Omit<Deliverable, 'id' | 'created_at' | 'updated_at' | 'is_overdue'>) {
    const { data: created, error } = await supabase
      .from('deliverables')
      .insert(data)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!created) throw new Error('Failed to create deliverable');
    return created;
  },

  /**
   * Update deliverable
   */
  async update(id: string, data: Partial<Deliverable>) {
    const { data: updated, error } = await supabase
      .from('deliverables')
      .update(data)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!updated) throw new Error('Deliverable not found or update failed');
    return updated;
  },

  /**
   * Update deliverable status
   */
  async updateStatus(id: string, status: DeliverableStatus) {
    // If status is Published, set published_at
    const updates: any = { status };
    if (status === 'Published' && !updates.published_at) {
      updates.published_at = new Date().toISOString();
    }

    return this.update(id, updates);
  },

  /**
   * Delete deliverable
   */
  async delete(id: string) {
    const { error } = await supabase
      .from('deliverables')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get single deliverable by ID
   */
  async getById(id: string): Promise<Deliverable | null> {
    const { data, error } = await supabase
      .from('deliverables')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Mark deliverables as overdue
   */
  async markOverdue() {
    const { error } = await supabase.rpc('mark_overdue_items');
    if (error) throw error;
  },

  /**
   * Get overdue deliverables
   */
  async getOverdue(): Promise<DeliverableWithInfluencer[]> {
    const { data, error } = await supabase
      .from('deliverables')
      .select(`
        *,
        campaign_influencers!left(influencer_name),
        campaigns!inner(work_code, client)
      `)
      .eq('is_overdue', true)
      .order('due_date', { ascending: true });

    if (error) throw error;

    return (data || []).map((d: any) => ({
      ...d,
      influencer_name: d.campaign_influencers?.influencer_name || null,
      days_until_due: d.due_date ? this.calculateDaysUntilDue(d.due_date) : null,
    }));
  },

  /**
   * Get deliverables due soon (within 3 days)
   */
  async getDueSoon(): Promise<DeliverableWithInfluencer[]> {
    const today = new Date();
    const threeDaysFromNow = new Date(today);
    threeDaysFromNow.setDate(today.getDate() + 3);

    const { data, error } = await supabase
      .from('deliverables')
      .select(`
        *,
        campaign_influencers!left(influencer_name),
        campaigns!inner(work_code, client)
      `)
      .gte('due_date', today.toISOString().split('T')[0])
      .lte('due_date', threeDaysFromNow.toISOString().split('T')[0])
      .neq('status', 'Published')
      .order('due_date', { ascending: true });

    if (error) throw error;

    return (data || []).map((d: any) => ({
      ...d,
      influencer_name: d.campaign_influencers?.influencer_name || null,
      days_until_due: d.due_date ? this.calculateDaysUntilDue(d.due_date) : null,
    }));
  },

  /**
   * Calculate days until due (negative if overdue)
   */
  calculateDaysUntilDue(dueDate: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  /**
   * Bulk create deliverables
   */
  async bulkCreate(deliverables: Omit<Deliverable, 'id' | 'created_at' | 'updated_at' | 'is_overdue'>[]) {
    const { data, error } = await supabase
      .from('deliverables')
      .insert(deliverables)
      .select();

    if (error) throw error;
    return data;
  },

  /**
   * Get unbilled deliverables for a campaign (not linked to any active Client invoice)
   */
  async getUnbilledDeliverables(campaignId: string): Promise<DeliverableWithPaymentStatus[]> {
    log.debug('[DeliverableService] Fetching unbilled deliverables for campaign:', campaignId);

    try {
      const { data, error } = await supabase.rpc('get_unbilled_deliverables', {
        p_campaign_id: campaignId
      });

      if (error) {
        log.error('[DeliverableService] RPC error, falling back to direct query:', error);
        return this.getUnbilledDeliverablesFallback(campaignId);
      }

      log.debug('[DeliverableService] RPC returned', data?.length || 0, 'deliverables');
      return (data || []).map((d: any) => ({
        ...d,
        is_client_paid: false,
        is_billed: false,
      }));
    } catch (err) {
      log.error('[DeliverableService] Exception in RPC, using fallback:', err);
      return this.getUnbilledDeliverablesFallback(campaignId);
    }
  },

  /**
   * Fallback: Get all deliverables for a campaign (when RPC is unavailable)
   */
  async getUnbilledDeliverablesFallback(campaignId: string): Promise<DeliverableWithPaymentStatus[]> {
    log.debug('[DeliverableService] Using fallback query for campaign:', campaignId);

    const { data, error } = await supabase
      .from('deliverables')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (error) {
      log.error('[DeliverableService] Fallback query error:', error);
      return [];
    }

    log.debug('[DeliverableService] Fallback returned', data?.length || 0, 'deliverables');
    return (data || []).map((d: any) => ({
      ...d,
      is_client_paid: false,
      is_billed: false,
    }));
  },

  /**
   * Get deliverables with payment status for a campaign
   */
  async getWithPaymentStatus(campaignId: string): Promise<DeliverableWithPaymentStatus[]> {
    const { data, error } = await supabase
      .from('deliverables_payment_status')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get payable amount for influencers in a campaign
   */
  async getInfluencerPayableAmount(campaignId: string, influencerId?: string) {
    const { data, error } = await supabase.rpc('get_influencer_payable_amount', {
      p_campaign_id: campaignId,
      p_influencer_id: influencerId || null
    });

    if (error) throw error;
    return data || [];
  },

  /**
   * Link deliverables to an invoice
   */
  async linkToInvoice(invoiceId: string, deliverables: { deliverableId: string; fee: number }[]) {
    const records = deliverables.map(d => ({
      invoice_id: invoiceId,
      deliverable_id: d.deliverableId,
      fee_at_invoice: d.fee
    }));

    const { data, error } = await supabase
      .from('invoice_deliverables')
      .insert(records)
      .select();

    if (error) throw error;
    return data;
  },

  /**
   * Get deliverables linked to an invoice
   */
  async getByInvoice(invoiceId: string): Promise<{ deliverable: Deliverable; fee_at_invoice: number }[]> {
    const { data, error } = await supabase
      .from('invoice_deliverables')
      .select(`
        fee_at_invoice,
        deliverables(*)
      `)
      .eq('invoice_id', invoiceId);

    if (error) throw error;

    return (data || []).map((d: any) => ({
      deliverable: d.deliverables,
      fee_at_invoice: d.fee_at_invoice
    }));
  },

  async syncInvoiceLinks(invoiceId: string, items: { deliverableId: string; fee: number }[]) {
    const { error: delError } = await supabase
      .from('invoice_deliverables')
      .delete()
      .eq('invoice_id', invoiceId);
    if (delError) throw delError;
    if (items.length === 0) return [];
    const records = items.map(d => ({
      invoice_id: invoiceId,
      deliverable_id: d.deliverableId,
      fee_at_invoice: d.fee,
    }));
    const { data, error } = await supabase
      .from('invoice_deliverables')
      .insert(records)
      .select();
    if (error) throw error;
    return data;
  },

  async getSelectedIdsByInvoice(invoiceId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('invoice_deliverables')
      .select('deliverable_id')
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return (data || []).map((r: any) => r.deliverable_id);
  },
};

export interface DeliverableWithPaymentStatus extends Deliverable {
  is_client_paid: boolean;
  is_billed: boolean;
  active_invoice_id?: string | null;
  paid_fee?: number | null;
}
